"use client";

import { ProductBadges } from "@/ui/components/catalog/product-badges";
import { publicStorePath } from "@/lib/storefront/public-url";
import { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { defaultLocale, formatPrice, isValidLocale, type Locale } from "@/config/locale";
import { normalizeSaleorMediaUrl } from "@/lib/media-url";
import { fetchPricingResolveItems, pricingItemsById } from "@/lib/pricing/pricing-resolve-client";
import type { ResolvedMoneyPresentation } from "@/lib/pricing/resolve-display-money";
import { useRecentlyViewed, type RecentlyViewedItem } from "@/lib/use-recently-viewed";
import { ReferencePrimaryPrice } from "@/ui/components/pricing/reference-primary-price";

interface RecentlyViewedProps {
	/** Current product to add to recently viewed + exclude from display */
	currentProduct: RecentlyViewedItem;
	locale: string;
	channel: string;
}

export function RecentlyViewed({ currentProduct, locale, channel }: RecentlyViewedProps) {
	const { items, add, mounted } = useRecentlyViewed();
	const t = useTranslations("recentlyViewed");
	const ploc = locale && isValidLocale(locale) ? (locale as Locale) : defaultLocale;
	const [resolved, setResolved] = useState<{
		requestKey: string;
		prices: Map<string, ResolvedMoneyPresentation>;
	} | null>(null);

	// Add current product to recently viewed on mount
	useEffect(() => {
		if (mounted) {
			add(currentProduct);
		}
		// Only run when mounted, not on every currentProduct change
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mounted, currentProduct.slug]);

	// Filter out current product from display
	const displayItems = useMemo(
		() => items.filter((item) => item.slug !== currentProduct.slug),
		[items, currentProduct.slug],
	);

	const priced = useMemo(
		() => displayItems.filter((i): i is RecentlyViewedItem & { price: number } => i.price !== null),
		[displayItems],
	);

	const priceItems = useMemo(
		() => priced.map((item) => ({ id: item.slug, amount: item.price, currency: item.currency })),
		[priced],
	);
	const requestKey = JSON.stringify({ locale, channel, items: priceItems });
	// A completed request belongs only to the exact locale, channel and stored prices.
	const pricesBySlug = priced.length > 0 && resolved?.requestKey === requestKey ? resolved.prices : null;

	useEffect(() => {
		if (!mounted || priced.length === 0) return;

		let cancelled = false;
		(async () => {
			const rows = await fetchPricingResolveItems({
				locale,
				channel,
				items: priceItems,
			});
			if (cancelled || !rows) return;
			setResolved({ requestKey, prices: pricingItemsById(rows) });
		})();

		return () => {
			cancelled = true;
		};
	}, [mounted, priced.length, priceItems, locale, channel, requestKey]);

	if (!mounted || displayItems.length === 0) {
		return null;
	}

	return (
		<section className="mt-12 border-t border-border pt-8">
			<h2 className="mb-6 text-lg font-semibold tracking-tight">{t("title")}</h2>
			<div className="scrollbar-thin flex gap-4 overflow-x-auto pb-4">
				{displayItems.map((item) => {
					const pricePres = pricesBySlug?.get(item.slug);
					return (
						<Link
							key={item.slug}
							href={`${publicStorePath(locale, `/products/${item.slug}`)}`}
							className="group flex w-36 shrink-0 flex-col sm:w-44"
						>
							<div className="relative aspect-square overflow-hidden bg-white">
								{item.image ? (
									<Image
										src={normalizeSaleorMediaUrl(item.image)}
										alt={item.name}
										fill
										sizes="(max-width: 640px) 144px, 176px"
										className="group-hover:scale-105 object-contain transition-transform duration-300"
									/>
								) : (
									<div className="flex h-full w-full items-center justify-center text-muted-foreground">
										<span className="text-xs">No image</span>
									</div>
								)}
							</div>
							<p className="group-hover:underline mt-2 line-clamp-2 text-sm font-medium">{item.name}</p>
							<ProductBadges slug={item.slug} />
							{item.reviewAvg !== undefined && item.reviewCount !== undefined && item.reviewCount > 0 && (
								<div className="mt-1 flex items-center gap-1">
									<svg
										className="h-3.5 w-3.5 fill-amber-400 text-amber-400"
										viewBox="0 0 20 20"
										aria-hidden="true"
									>
										<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
									</svg>
									<span className="text-xs font-medium tabular-nums">{item.reviewAvg.toFixed(1)}</span>
									<span className="text-xs text-muted-foreground">({item.reviewCount})</span>
								</div>
							)}
							{item.price !== null && (
								<p className="mt-1 text-sm text-muted-foreground">
									{pricePres ? (
										<ReferencePrimaryPrice
											presentation={pricePres}
											locale={locale}
											channel={channel}
											size="sm"
											className="text-sm text-muted-foreground"
										/>
									) : (
										formatPrice(item.price, item.currency, ploc)
									)}
								</p>
							)}
						</Link>
					);
				})}
			</div>
		</section>
	);
}
